import { useCallback, useEffect, useRef, useState } from 'react';
import { cssVar, motion } from '@going-once/tokens';
import { Icon, type IconName } from './icons';
import { Card, focusRing, Pressable, Stack, Text } from './primitives';

/**
 * Loading and error, as components rather than as inline ternaries in every screen.
 *
 * The error one earns its place: it shows the capability's own message, and the request
 * id underneath it. Every error this app can produce is already a sentence written for a
 * person — throwing that away and rendering "Something went wrong" is a choice, and the
 * request id is what turns a support message into a log query.
 */

export function Loading({ what }: { what: string }) {
  return (
    <Text tone="fg.muted" step="label">
      Loading {what}…
    </Text>
  );
}

export function ErrorNote({
  error,
  onRetry,
}: {
  error: { message: string; code?: string; requestId?: string };
  onRetry?: () => void;
}) {
  return (
    <Card>
      <Stack gap="sm" align="start">
        <Text tone="danger">{error.message}</Text>
        {error.requestId ? (
          <Text step="label" tone="fg.muted">
            {error.code ? `${error.code} · ` : ''}
            {error.requestId}
          </Text>
        ) : null}
        {onRetry ? (
          <Pressable variant="secondary" onPress={onRetry}>
            Try again
          </Pressable>
        ) : null}
      </Stack>
    </Card>
  );
}

/**
 * Nothing to show yet, or nothing matches (the design system's `EmptyState`): an icon on a
 * neutral disc, a title, a line saying what will appear and how, and at most one action.
 */
export function EmptyState({
  icon = 'inbox',
  title,
  body,
  action,
}: {
  icon?: IconName;
  title: string;
  body?: string | undefined;
  action?: { label: string; onPress: () => void } | undefined;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: cssVar('space.sm'),
        padding: `${cssVar('space.2xl')} ${cssVar('space.lg')}`,
        textAlign: 'center',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: 'grid',
          placeItems: 'center',
          width: cssVar('size.empty.icon'),
          height: cssVar('size.empty.icon'),
          borderRadius: cssVar('radius.full'),
          background: cssVar('color.neutral.tint'),
          color: cssVar('color.fg.muted'),
        }}
      >
        <Icon name={icon} />
      </span>
      <Text as="p" step="title">
        {title}
      </Text>
      {body ? (
        <span style={{ maxWidth: cssVar('size.empty.body') }}>
          <Text as="p" step="label" tone="fg.muted">
            {body}
          </Text>
        </span>
      ) : null}
      {action ? (
        <Pressable variant="quiet" onPress={action.onPress}>
          {action.label}
        </Pressable>
      ) : null}
    </div>
  );
}

/** Why a toast is waiting: the pointer is on it, or keyboard focus is in it. Either alone holds it. */
type Hold = 'pointer' | 'focus';

/**
 * A toast's clock, React-free so it can be tested on its own: `show` starts the full
 * duration (again, for a message flashed while one shows), a hold stops it, and the last
 * release starts the **full** duration again — not the remainder — as the design system
 * does. `stop` forgets everything, holds included: a toast that leaves while the pointer is
 * on it gets no pointer-leave, and its hold must not outlive it onto the next message.
 */
export class DismissClock {
  private readonly after: number;
  private readonly onDismiss: () => void;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private showing = false;
  private readonly holds = new Set<Hold>();

  constructor(after: number, onDismiss: () => void) {
    this.after = after;
    this.onDismiss = onDismiss;
  }

  show(): void {
    this.showing = true;
    this.run();
  }

  hold(why: Hold): void {
    this.holds.add(why);
    this.clear();
  }

  release(why: Hold): void {
    if (!this.holds.delete(why)) return;
    this.run();
  }

  stop(): void {
    this.showing = false;
    this.holds.clear();
    this.clear();
  }

  private run(): void {
    this.clear();
    if (!this.showing || this.holds.size > 0) return;
    this.timer = setTimeout(() => {
      this.timer = undefined;
      this.showing = false;
      this.onDismiss();
    }, this.after);
  }

  private clear(): void {
    if (this.timer !== undefined) clearTimeout(this.timer);
    this.timer = undefined;
  }
}

/** One flash: the words, and which flash it is, so the same words flashed twice are two toasts. */
export interface ToastMessage {
  message: string;
  id: number;
}

/** A page's toast: what to pass `Toast`, and the two things a page does with it. */
export function useToast(): { toast: ToastMessage | undefined; flash: (message: string) => void; dismiss: () => void } {
  const [toast, setToast] = useState<ToastMessage | undefined>(undefined);
  const flash = useCallback((message: string) => setToast((current) => ({ message, id: (current?.id ?? 0) + 1 })), []);
  const dismiss = useCallback(() => setToast(undefined), []);
  return { toast, flash, dismiss };
}

/**
 * The confirmation that an action just happened (the design system's `Toast`, success
 * tone): bottom-left, on the sheet, gone on its own after `motion.duration.toast` (4 s).
 * Use it with `useToast`.
 *
 * **The live region is always on the page**, and the message comes and goes inside it.
 * A `role="status"` element that arrives already holding its text is not reliably
 * announced, which is the one job a toast has for somebody who cannot see it.
 *
 * One at a time: a new flash replaces the one showing and restarts the clock, even with
 * the same words. It waits while the pointer or keyboard is on it, and ✕ dismisses it at once.
 */
export function Toast({ toast, onDismiss }: { toast: ToastMessage | undefined; onDismiss: () => void }) {
  const [focused, setFocused] = useState(false);
  // The newest `onDismiss`, without restarting the clock each time the caller re-renders
  // with a fresh closure — which would hold a toast on screen for as long as the page is busy.
  const latest = useRef(onDismiss);
  latest.current = onDismiss;
  const clock = useRef<DismissClock | null>(null);
  clock.current ??= new DismissClock(motion.duration.toast, () => latest.current());

  // Each flash is a new object, so the same words flashed twice restart the clock.
  useEffect(() => {
    if (toast) clock.current?.show();
    else clock.current?.stop();
  }, [toast]);
  useEffect(() => () => clock.current?.stop(), []);

  return (
    <div
      role="status"
      onPointerEnter={() => clock.current?.hold('pointer')}
      onPointerLeave={() => clock.current?.release('pointer')}
      onFocus={() => clock.current?.hold('focus')}
      onBlur={() => clock.current?.release('focus')}
      style={{
        position: 'fixed',
        left: cssVar('space.lg'),
        bottom: cssVar('space.lg'),
        zIndex: 60,
        maxWidth: `min(${cssVar('size.toast')}, calc(100% - 2 * ${cssVar('space.lg')}))`,
      }}
    >
      {toast !== undefined ? (
        <ToastBody message={toast.message} focused={focused} onFocused={setFocused} onDismiss={onDismiss} />
      ) : null}
    </div>
  );
}

function ToastBody({
  message,
  focused,
  onFocused,
  onDismiss,
}: {
  message: string;
  focused: boolean;
  onFocused: (focused: boolean) => void;
  onDismiss: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: cssVar('space.sm'),
        boxSizing: 'border-box',
        padding: `${cssVar('space.xs')} ${cssVar('space.xs')} ${cssVar('space.xs')} ${cssVar('space.md')}`,
        background: cssVar('color.bg.sheet'),
        color: cssVar('color.fg.default'),
        border: `1px solid ${cssVar('color.border.strong')}`,
        borderRadius: cssVar('radius.lg'),
        boxShadow: cssVar('elevation.overlay'),
      }}
    >
      <span aria-hidden="true" style={{ display: 'inline-flex', color: cssVar('color.success') }}>
        <Icon name="circle-check" />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <Text>{message}</Text>
      </span>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onDismiss}
        onFocus={(event) => onFocused(event.currentTarget.matches(':focus-visible'))}
        onBlur={() => onFocused(false)}
        style={{
          display: 'inline-grid',
          placeItems: 'center',
          flexShrink: 0,
          width: cssVar('size.target.min'),
          height: cssVar('size.target.min'),
          padding: 0,
          border: 'none',
          borderRadius: cssVar('radius.md'),
          background: 'transparent',
          color: cssVar('color.fg.muted'),
          cursor: 'pointer',
          ...(focused ? focusRing : {}),
        }}
      >
        <Icon name="x" />
      </button>
    </div>
  );
}
